/**
 * Voiceprint enrollment recorder.
 *
 * Captures up to `maxSeconds` of microphone audio, decodes via AudioContext,
 * re-samples to 16 kHz mono, and emits a base64-encoded 16-bit PCM WAV — the
 * format HEIDI.LLM's `/v1/voiceprint/extract` expects.
 *
 * Deliberately NOT using the existing WebSocket/streaming recorder — this is
 * a one-shot blob capture with client-side format conversion, not a streaming
 * session. Keeps the enrollment page simple and fully REST-based.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type VoiceprintRecorderState =
  | 'idle'
  | 'requesting_mic'
  | 'recording'
  | 'processing'
  | 'ready'
  | 'error';

interface CapturedVoiceprint {
  /** Base64-encoded WAV bytes (16 kHz mono, 16-bit PCM). */
  wavBase64: string;
  durationSec: number;
  sampleRate: number;
  byteLength: number;
}

interface UseVoiceprintRecorderOptions {
  maxSeconds?: number;
  targetSampleRate?: number;
}

const TARGET_SAMPLE_RATE = 16000;

export function useVoiceprintRecorder(opts: UseVoiceprintRecorderOptions = {}) {
  const maxSeconds = opts.maxSeconds ?? 30;
  const targetSampleRate = opts.targetSampleRate ?? TARGET_SAMPLE_RATE;

  const [state, setState] = useState<VoiceprintRecorderState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [captured, setCaptured] = useState<CapturedVoiceprint | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanupMedia = useCallback(() => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  }, []);

  // Always release the mic if the component unmounts mid-capture.
  useEffect(() => cleanupMedia, [cleanupMedia]);

  const start = useCallback(async () => {
    setError(null);
    setElapsedMs(0);
    setCaptured(null);
    setState('requesting_mic');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Microphone access denied or unavailable: ${msg}`);
      setState('error');
      return;
    }
    streamRef.current = stream;

    const mr = new MediaRecorder(stream);
    chunksRef.current = [];
    mr.ondataavailable = (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    mr.onstop = async () => {
      setState('processing');
      try {
        const raw = new Blob(chunksRef.current, {
          type: chunksRef.current[0]?.type || 'audio/webm',
        });
        const wavBase64 = await blobToPcm16WavBase64(raw, targetSampleRate);
        // Duration in seconds: bytes - 44 header / (sampleRate * 2 bytes per sample).
        const bytes = Math.floor((wavBase64.length * 3) / 4);
        const pcmBytes = Math.max(0, bytes - 44);
        const duration = pcmBytes / (targetSampleRate * 2);
        setCaptured({
          wavBase64,
          durationSec: Math.round(duration * 10) / 10,
          sampleRate: targetSampleRate,
          byteLength: bytes,
        });
        setState('ready');
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(`Failed to convert recording to WAV: ${msg}`);
        setState('error');
      } finally {
        cleanupMedia();
      }
    };

    mediaRecorderRef.current = mr;
    startedAtRef.current = Date.now();
    mr.start();
    setState('recording');

    tickerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 100);

    autoStopTimerRef.current = setTimeout(() => {
      if (mr.state !== 'inactive') mr.stop();
    }, maxSeconds * 1000);
  }, [cleanupMedia, maxSeconds, targetSampleRate]);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
  }, []);

  const reset = useCallback(() => {
    cleanupMedia();
    setState('idle');
    setElapsedMs(0);
    setCaptured(null);
    setError(null);
  }, [cleanupMedia]);

  return { state, error, elapsedMs, captured, maxSeconds, start, stop, reset };
}

/**
 * Decode any browser-supported audio blob (webm/opus/ogg/mp4) via AudioContext,
 * downmix to mono, resample to `targetSampleRate`, and return a 16-bit PCM WAV
 * as base64. No external dependencies.
 */
async function blobToPcm16WavBase64(
  blob: Blob,
  targetSampleRate: number,
): Promise<string> {
  const arrayBuffer = await blob.arrayBuffer();
  const AudioContextCtor =
    (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('AudioContext is not available in this browser');
  }
  // Decode with a throwaway context so the sample rate is whatever the source is.
  const decodeCtx = new AudioContextCtor();
  let audioBuffer: AudioBuffer;
  try {
    audioBuffer = await decodeCtx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    void decodeCtx.close();
  }

  // Downmix → mono by averaging channels.
  const numChannels = audioBuffer.numberOfChannels;
  const srcLen = audioBuffer.length;
  const mono = new Float32Array(srcLen);
  for (let c = 0; c < numChannels; c++) {
    const chan = audioBuffer.getChannelData(c);
    for (let i = 0; i < srcLen; i++) mono[i] += chan[i];
  }
  if (numChannels > 1) {
    for (let i = 0; i < srcLen; i++) mono[i] /= numChannels;
  }

  // Resample via OfflineAudioContext.
  const srcRate = audioBuffer.sampleRate;
  let resampled: Float32Array;
  if (srcRate === targetSampleRate) {
    resampled = mono;
  } else {
    const ratio = targetSampleRate / srcRate;
    const outLen = Math.ceil(srcLen * ratio);
    const offline = new OfflineAudioContext(1, outLen, targetSampleRate);
    const srcBuffer = offline.createBuffer(1, srcLen, srcRate);
    srcBuffer.copyToChannel(mono, 0);
    const node = offline.createBufferSource();
    node.buffer = srcBuffer;
    node.connect(offline.destination);
    node.start();
    const rendered = await offline.startRendering();
    resampled = rendered.getChannelData(0).slice(0);
  }

  // Encode as 16-bit PCM WAV.
  const wavBytes = encodeWavPcm16(resampled, targetSampleRate);
  return bytesToBase64(wavBytes);
}

function encodeWavPcm16(samples: Float32Array, sampleRate: number): Uint8Array {
  const numFrames = samples.length;
  const numChannels = 1;
  const byteRate = sampleRate * numChannels * 2;
  const blockAlign = numChannels * 2;
  const dataSize = numFrames * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  let offset = 0;

  const writeStr = (s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i));
  };
  const writeU32 = (v: number) => {
    view.setUint32(offset, v, true);
    offset += 4;
  };
  const writeU16 = (v: number) => {
    view.setUint16(offset, v, true);
    offset += 2;
  };

  writeStr('RIFF');
  writeU32(36 + dataSize);
  writeStr('WAVE');
  writeStr('fmt ');
  writeU32(16); // fmt chunk size
  writeU16(1); // PCM
  writeU16(numChannels);
  writeU32(sampleRate);
  writeU32(byteRate);
  writeU16(blockAlign);
  writeU16(16); // bits per sample
  writeStr('data');
  writeU32(dataSize);

  for (let i = 0; i < numFrames; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return new Uint8Array(buffer);
}

function bytesToBase64(bytes: Uint8Array): string {
  // Encode in 8 KB chunks to avoid arg-count limits on String.fromCharCode.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      bytes.subarray(i, i + CHUNK) as unknown as any,
    );
  }
  return btoa(binary);
}
